import { karmakandaAPI } from '../services/api';
import type { Bidhi } from '../types';
import { normalizeBidhiListFromApi } from './normalizeBidhiFromApi';

export type BidhiCatalogOption = {
  value: number;
  label: string;
};

export function buildBidhiNameById(bidhis: Bidhi[]): Record<number, string> {
  const map: Record<number, string> = {};
  bidhis.forEach((bidhi) => {
    if (bidhi.id != null && bidhi.name) {
      map[bidhi.id] = bidhi.name;
    }
  });
  return map;
}

export function buildBidhiCatalogOptions(bidhis: Bidhi[]): BidhiCatalogOption[] {
  return bidhis
    .filter((bidhi) => bidhi.id != null && bidhi.name)
    .map((bidhi) => ({
      value: bidhi.id,
      label: bidhi.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export async function loadBidhiCatalog(): Promise<{
  bidhis: Bidhi[];
  nameById: Record<number, string>;
  options: BidhiCatalogOption[];
}> {
  try {
    const response = await karmakandaAPI.getBidhiCatalog();
    const rows = Array.isArray(response.data) ? response.data : [];
    const bidhis = rows.map(
      (row: any) =>
        ({
          id: row.id,
          name: row.name,
          profilePicture: row.profilePicture,
          defaultColor: row.defaultColor,
          metadata: row.metadata,
          isPuja: row.isPuja,
          isPublished: row.isPublished,
          isTitleHidden: row.isTitleHidden,
          bishesh: row.bishesh,
          nisedh: row.nisedh,
          tags: row.tags || [],
          lineCount: row.lineCount ?? 0,
          lines: [],
          children: [],
          samagriAssociations: [],
          brahmanYajamanKartabyaAssociations: [],
          // Marks catalog stub until full list / detail hydrates
          _catalogStub: true,
        }) as Bidhi,
    );
    return {
      bidhis,
      nameById: buildBidhiNameById(bidhis),
      options: buildBidhiCatalogOptions(bidhis),
    };
  } catch {
    // Fallback for older backends without /bidhi/catalog.
    const response = await karmakandaAPI.getAllBidhisDeduped().catch(() => karmakandaAPI.getAllBidhis());
    const bidhis = normalizeBidhiListFromApi(response.data);
    return {
      bidhis,
      nameById: buildBidhiNameById(bidhis),
      options: buildBidhiCatalogOptions(bidhis),
    };
  }
}

export function normalizeApprovedBidhiIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const ids = value
    .map((item) => Number(item))
    .filter((id) => Number.isFinite(id) && id > 0);
  return Array.from(new Set(ids));
}
